import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Filter, Save } from "lucide-react";
import { apiRequest, downloadFile } from "@/api/client";

const STATUS_OPTIONS = ["new", "contacted", "demo_scheduled", "won", "lost"];
const STATUS_TONES = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-amber-100 text-amber-800",
  demo_scheduled: "bg-purple-100 text-purple-800",
  won: "bg-emerald-100 text-emerald-800",
  lost: "bg-slate-200 text-slate-700",
};

export default function LeadCrm() {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ status: "new", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/leads?status=${filter}` : "/admin/leads";
      const data = await apiRequest(url);
      setLeads(data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const startEdit = (lead) => {
    setEditingId(lead.id);
    setEditForm({ status: lead.status, notes: lead.notes || "" });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({ status: "new", notes: "" }); };

  const save = async (lead) => {
    try {
      await apiRequest(`/admin/leads/${lead.id}`, {
        method: "PATCH",
        body: JSON.stringify(editForm),
      });
      cancelEdit();
      await load();
    } catch (err) { setError(err.message); }
  };

  const exportCsv = async () => {
    try { await downloadFile("/admin/leads/export", "leads.csv"); }
    catch (err) { setError(err.message); }
  };

  const fmt = (iso) => iso ? iso.slice(0, 19).replace("T", " ") : "—";

  return (
    <div className="space-y-6" data-testid="lead-crm-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="lead-crm-title">{t("leads.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("leads.subtitle")}</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold hover:bg-slate-50" data-testid="lead-export-button">
          <Download className="h-4 w-4" /> {t("leads.export_csv")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="lead-filters">
        <Filter className="h-4 w-4 text-slate-500" />
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === "" ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid="lead-filter-all">{t("leads.filter_all")}</button>
        {STATUS_OPTIONS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === s ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`lead-filter-${s}`}>{t(`leads.status_${s}`)}</button>
        ))}
      </div>

      {error && <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-800" data-testid="lead-crm-error">{error}</div>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="lead-crm-loading">{t("common.loading")}</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="lead-crm-empty">{t("leads.no_leads")}</div>
        ) : (
          <table className="w-full text-sm" data-testid="lead-crm-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2.5">{t("common.date")}</th>
                <th className="px-3 py-2.5">{t("common.name")}</th>
                <th className="px-3 py-2.5">Email</th>
                <th className="px-3 py-2.5">{t("common.company")}</th>
                <th className="px-3 py-2.5">{t("common.status")}</th>
                <th className="px-3 py-2.5">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <tr key={lead.id} className="border-t border-slate-100 align-top" data-testid={`lead-row-${lead.id}`}>
                  <td className="px-3 py-2.5 text-xs text-slate-500 font-mono">{fmt(lead.created_at)}</td>
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-slate-800">{lead.name}</p>
                    {lead.role && <p className="text-xs text-slate-500">{lead.role}</p>}
                  </td>
                  <td className="px-3 py-2.5">
                    <a href={`mailto:${lead.email}`} className="text-blue-700 hover:underline" data-testid={`lead-email-${lead.id}`}>{lead.email}</a>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{lead.company}</td>
                  <td className="px-3 py-2.5">
                    {editingId === lead.id ? (
                      <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`lead-status-select-${lead.id}`}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{t(`leads.status_${s}`)}</option>)}
                      </select>
                    ) : (
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_TONES[lead.status] || STATUS_TONES.new}`} data-testid={`lead-status-badge-${lead.id}`}>{t(`leads.status_${lead.status}`)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {editingId === lead.id ? (
                      <div className="space-y-2">
                        <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder={t("leads.notes_placeholder")} rows={2} className="w-48 rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`lead-notes-input-${lead.id}`} />
                        <div className="flex gap-1">
                          <button onClick={() => save(lead)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-xs font-semibold text-white hover:bg-emerald-700" data-testid={`lead-save-${lead.id}`}><Save className="h-3 w-3" /> {t("leads.save")}</button>
                          <button onClick={cancelEdit} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50" data-testid={`lead-cancel-${lead.id}`}>{t("common.cancel")}</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(lead)} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`lead-edit-${lead.id}`}>{t("common.edit")}</button>
                    )}
                    {lead.notes && editingId !== lead.id && <p className="mt-1 max-w-xs text-xs text-slate-500" title={lead.notes}>{lead.notes.slice(0, 60)}{lead.notes.length > 60 && "…"}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
